import React from 'react';

import { useTranslation } from 'react-i18next';
import { Button, FormInstance, FormItem, FormList, InputNumber, Label } from '@bringg/react-components';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';

import { RowFormValue } from '../../types';
import { HOURS_MAX, HOURS_MIN, RATIO_PERCENT_MAX, RATIO_PERCENT_MIN } from '../../constants';
import { buildHoursFrequencyMap } from '../../helpers';

import styles from './day-section.module.scss';

type Props = {
	name: string | (string | number)[];
	title: string;
	abbreviation?: string;
	form: FormInstance;
};

export const DaySection = ({ name, title, abbreviation, form }: Props) => {
	const { t } = useTranslation();

	const hoursDuplicateValidator = (_: unknown, value: number) => {
		const rows = (form.getFieldValue(name) as RowFormValue[]) ?? [];
		const hoursMap = buildHoursFrequencyMap(rows);

		return (hoursMap.get(value) ?? 0) > 1
			? Promise.reject(new Error(t('RESERVE_CAPACITY.HOURS_DUPLICATE_ERROR')))
			: Promise.resolve();
	};

	return (
		<FormList name={name}>
			{(fields, { add, remove }) => (
				<div className={styles.daySection}>
					<div className={styles.daySectionHeader}>
						<div className={styles.daySectionTitleGroup}>
							{abbreviation && <span className={styles.daySectionBadge}>{abbreviation}</span>}
							<span className={styles.daySectionTitle}>{title}</span>
						</div>
						<Button
							type="inline-link"
							className={styles.daySectionAddButton}
							onClick={() => add({ ratio: 0, reserved_until_hours: 0 })}
						>
							<BringgIcon iconName={BringgFontIcons.Plus} />
							{t('RESERVE_CAPACITY.ADD')}
						</Button>
					</div>
					<div className={styles.daySectionBody}>
						{fields.map(field => (
							<div key={field.key} className={styles.daySectionInputRow}>
								<div className={styles.daySectionInputField}>
									<Label
										text={t('RESERVE_CAPACITY.RESERVE_PERCENT')}
										size="small"
										className={styles.daySectionInputLabel}
									/>
									<FormItem
										name={[field.name, 'ratio']}
										rules={[
											{
												type: 'number',
												min: RATIO_PERCENT_MIN,
												max: RATIO_PERCENT_MAX,
												message: t('RESERVE_CAPACITY.RATIO_RANGE_ERROR', {
													min: RATIO_PERCENT_MIN,
													max: RATIO_PERCENT_MAX
												})
											},
											{ required: true, message: t('RESERVE_CAPACITY.REQUIRED_ERROR') }
										]}
										validateTrigger={['onChange', 'onBlur']}
									>
										<InputNumber
											id={`ratio-input-${field.key}`}
											className={styles.daySectionNumberInput}
										/>
									</FormItem>
								</div>
								<div className={styles.daySectionInputField}>
									<Label
										text={t('RESERVE_CAPACITY.HOURS_BEFORE_DELIVERY')}
										size="small"
										className={styles.daySectionInputLabel}
									/>
									<FormItem
										name={[field.name, 'reserved_until_hours']}
										rules={[
											{
												type: 'integer',
												min: HOURS_MIN,
												max: HOURS_MAX,
												message: t('RESERVE_CAPACITY.HOURS_RANGE_ERROR', {
													min: HOURS_MIN,
													max: HOURS_MAX
												})
											},
											{ required: true, message: t('RESERVE_CAPACITY.REQUIRED_ERROR') },
											{ validator: hoursDuplicateValidator }
										]}
										validateFirst
										validateTrigger={['onChange', 'onBlur']}
									>
										<InputNumber
											id={`hours-input-${field.key}`}
											className={styles.daySectionNumberInput}
										/>
									</FormItem>
								</div>
								<Button
									type="link"
									data-test-id="delete-row-button"
									className={styles.daySectionDeleteButton}
									onClick={() => remove(field.name)}
									disabled={fields.length <= 1}
								>
									<BringgIcon iconName={BringgFontIcons.Trash} />
								</Button>
							</div>
						))}
					</div>
				</div>
			)}
		</FormList>
	);
};
